import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Package, Truck, ChefHat, AlertTriangle, ClipboardList,
  Store, TrendingUp, TrendingDown, RefreshCw, Link2Off,
  Bell, DollarSign, ShoppingCart, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const R = (v) => `R${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, notifRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/dashboard/notifications`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setNotifications(notifRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const alertRecipes = notifications?.cost_alerts?.recipes || [];
  const priceChanges = notifications?.recent_price_changes || [];
  const marginHealth = notifications?.margin_health || [];
  const pendingActions = notifications?.pending_actions || {};

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard">
      {/* Welcome */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground mt-1">{today}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/menu-products">
            <Button variant="outline" data-testid="sync-loyverse-btn">
              <RefreshCw className="w-4 h-4 mr-2" />Sync Loyverse
            </Button>
          </Link>
          <Link to="/stock-take">
            <Button data-testid="start-stocktake-btn" className="bg-accent hover:bg-accent/90">
              <ClipboardList className="w-4 h-4 mr-2" />Start Stock Take
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="hover-lift" data-testid="stat-products">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Menu Products</p>
                <p className="text-2xl font-bold">{stats?.menu_products_count || 0}</p>
                {stats?.unlinked_products_count > 0 && (
                  <p className="text-xs text-destructive">{stats.unlinked_products_count} unlinked</p>
                )}
              </div>
              <ChefHat className="w-5 h-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="hover-lift" data-testid="stat-items">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Stock Items</p>
                <p className="text-2xl font-bold">{stats?.stock_items_count || 0}</p>
              </div>
              <Package className="w-5 h-5 text-accent" />
            </div>
          </CardContent>
        </Card>
        <Card className="hover-lift" data-testid="stat-suppliers">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Suppliers</p>
                <p className="text-2xl font-bold">{stats?.suppliers_count || 0}</p>
              </div>
              <Truck className="w-5 h-5 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="hover-lift" data-testid="stat-alerts">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Cost Alerts</p>
                <p className="text-2xl font-bold text-destructive">{notifications?.cost_alerts?.count || 0}</p>
              </div>
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
          </CardContent>
        </Card>
        <Card className="hover-lift" data-testid="stat-pending-orders">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Pending Purchase Orders</p>
                <p className="text-2xl font-bold">{pendingActions.pending_orders || 0}</p>
              </div>
              <ShoppingCart className="w-5 h-5 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="hover-lift" data-testid="stat-unconfirmed">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Unconfirmed Takes</p>
                <p className="text-2xl font-bold">{pendingActions.unconfirmed_stock_takes || 0}</p>
              </div>
              <ClipboardList className="w-5 h-5 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notification Widgets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Margin Health by Category */}
        <Card data-testid="margin-health-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />Margin Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            {marginHealth.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No recipe data yet</p>
            ) : (
              <div className="space-y-3">
                {marginHealth.map((m, i) => {
                  const isOver = m.avg_cost_percentage > m.benchmark;
                  return (
                    <div key={i} className="p-3 rounded-lg border" data-testid={`margin-health-${m.category.toLowerCase()}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{m.category}</span>
                        <Badge variant={m.health === 'good' ? 'default' : m.health === 'warning' ? 'secondary' : 'destructive'} className="text-xs">
                          {m.health === 'good' ? 'Healthy' : m.health === 'warning' ? 'Caution' : 'Critical'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Avg: {m.avg_cost_percentage}% / {m.benchmark}% target</span>
                        <span>{m.alert_count}/{m.total_recipes} alerts</span>
                      </div>
                      <div className="mt-2 w-full bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${m.health === 'good' ? 'bg-green-500' : m.health === 'warning' ? 'bg-amber-500' : 'bg-destructive'}`}
                          style={{ width: `${Math.min((m.avg_cost_percentage / m.benchmark) * 100, 150)}%`, maxWidth: '100%' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost Alerts */}
        <Card className="lg:col-span-1" data-testid="cost-alerts-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-destructive" />Cost Alerts
              </CardTitle>
              {alertRecipes.length > 0 && (
                <Badge variant="destructive" className="text-xs">{notifications?.cost_alerts?.count}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {alertRecipes.length === 0 ? (
              <div className="text-center py-6">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-500 opacity-50" />
                <p className="text-sm text-muted-foreground">All recipes within benchmarks</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {alertRecipes.map((r) => (
                  <Link key={r.recipe_id} to={`/recipes/${r.recipe_id}`} className="block">
                    <div className="flex items-center justify-between p-2.5 rounded-lg border border-destructive/20 bg-destructive/5 hover:bg-destructive/10 transition-colors">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.actual_cost_percentage?.toFixed(1)}% vs {r.benchmark_cost_percentage}% target
                        </p>
                      </div>
                      <Badge variant="destructive" className="text-xs ml-2 shrink-0">
                        +{(r.actual_cost_percentage - r.benchmark_cost_percentage).toFixed(1)}%
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Price Changes */}
        <Card data-testid="price-changes-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4 text-blue-500" />Recent Price Changes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {priceChanges.length === 0 ? (
              <div className="text-center py-6">
                <DollarSign className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-30" />
                <p className="text-sm text-muted-foreground">No recent price changes</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {priceChanges.slice(0, 10).map((pc, i) => {
                  const isUp = pc.new_price > pc.old_price;
                  return (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border hover:bg-muted/50 transition-colors">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{pc.item_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {R(pc.old_price)} &rarr; {R(pc.new_price)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        {isUp ? (
                          <ArrowUpRight className="w-3.5 h-3.5 text-destructive" />
                        ) : (
                          <ArrowDownRight className="w-3.5 h-3.5 text-green-600" />
                        )}
                        <span className={`text-xs font-medium ${isUp ? 'text-destructive' : 'text-green-600'}`}>
                          {pc.change_percent > 0 ? '+' : ''}{pc.change_percent}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card data-testid="quick-actions-card">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Link to="/menu-products">
              <div className="p-4 rounded-lg border hover:bg-muted/50 hover:border-primary/30 transition-all text-center cursor-pointer">
                <ChefHat className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">Menu Products</p>
              </div>
            </Link>
            <Link to="/stock-items">
              <div className="p-4 rounded-lg border hover:bg-muted/50 hover:border-primary/30 transition-all text-center cursor-pointer">
                <Package className="w-8 h-8 mx-auto mb-2 text-accent" />
                <p className="text-sm font-medium">Stock Items</p>
              </div>
            </Link>
            <Link to="/stock-take">
              <div className="p-4 rounded-lg border hover:bg-muted/50 hover:border-primary/30 transition-all text-center cursor-pointer">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 text-green-600" />
                <p className="text-sm font-medium">Stock Take</p>
              </div>
            </Link>
            <Link to="/orders">
              <div className="p-4 rounded-lg border hover:bg-muted/50 hover:border-primary/30 transition-all text-center cursor-pointer">
                <Truck className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <p className="text-sm font-medium">Purchase Orders</p>
              </div>
            </Link>
            <Link to="/recipes">
              <div className="p-4 rounded-lg border hover:bg-muted/50 hover:border-primary/30 transition-all text-center cursor-pointer">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <p className="text-sm font-medium">Recipes</p>
              </div>
            </Link>
            <Link to="/reports">
              <div className="p-4 rounded-lg border hover:bg-muted/50 hover:border-primary/30 transition-all text-center cursor-pointer">
                <Store className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                <p className="text-sm font-medium">Reports</p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
